<template>
  <div class="card" style="min-height: calc(100vh - 180px)">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Fixed Asset Schedule"
      />
    </div>

    <div class="px-2 mt-2">
      <div class="row">
        <div v-if="isYearly" class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Financial Year (Start Year): </label>
          <v-select
              placeholder="Select Year"
              v-model="intervalValue"
              :options="yearOptions"
              :reduce="item => item.value"
              label="name"
          />
        </div>

        <div v-if="isQuarterly" class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Quarter: </label>
          <v-select
              placeholder="Select Quarter"
              v-model="intervalValue"
              :options="quarterOptions"
              :reduce="item => item.value"
              label="name"
          />
        </div>

        <div v-if="isMonthly" class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Month: </label>
          <input
              class="form-control"
              type="month"
              v-model="intervalValue"
          >
        </div>

        <template v-if="isDaily">
          <div class="col-12 col-sm-6 col-md-4 col-lg-3">
            <label class="col-form-label">Start Date: </label>
            <input
                class="form-control"
                type="date"
                v-model="startDate"
            >
          </div>
          <div class="col-12 col-sm-6 col-md-4 col-lg-3">
            <label class="col-form-label">End Date: </label>
            <input
                class="form-control"
                type="date"
                v-model="endDate"
            >
          </div>
        </template>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Group By: </label>
          <v-select
              placeholder="Select Group By"
              v-model="groupBy"
              :options="groupByOptions"
              :reduce="item => item.value"
              label="name"
              :clearable="false"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Asset Class: </label>
          <AsyncSelect
              placeholder="Select Asset Class"
              v-model="selectedProduct"
              :api-service="fetchProductList"
              :format-label="option => option.text"
              label="text"
              :additional-query="{ product_type: 'fixed_assets' }"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Location: </label>
          <AsyncSelect
              placeholder="Select Location"
              v-model="businessLocation"
              :api-service="fetchBusinessLocationsList"
              :format-label="option => option.text"
              label="text"
              :additional-query="{ location_type: 'fixed_assets' }"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Department: </label>
          <AsyncSelect
              placeholder="Select Department"
              v-model="department"
              :api-service="fetchDepartmentList"
              :format-label="option => option.name"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Project: </label>
          <AsyncSelect
              placeholder="Select Project"
              v-model="project"
              :api-service="fetchProjects"
              :format-label="option => option.name"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Cost Centre: </label>
          <AsyncSelect
              placeholder="Select Cost Centre"
              v-model="costCentre"
              :api-service="fetchCostCentreList"
              :format-label="option => option.name"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label class="col-form-label">Business Unit: </label>
          <AsyncSelect
              placeholder="Select Business Unit"
              v-model="business"
              :api-service="fetchBusinesses"
              :format-label="option => option.name"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label class="col-form-label">Custodian: </label>
            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
              <i class="fas fa-search"></i>
            </span>
          </div>
          <AsyncSelect
              placeholder="Select Custodian"
              v-model="contactProfile"
              :api-service="fetchContactProfiles"
              :additional-query="{type: 'employee'}"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3 d-flex align-items-end mt-1">
          <button
              :disabled="loading"
              class="btn btn-primary"
              @click="getReport"
          >
            Go
          </button>
        </div>
      </div>
    </div>

    <div class="col-12 px-2 mt-2 pb-2">
      <div class="card">
        <div class="table-responsive table-min-height">
          <table class="table table-bordered schedule-table">
            <thead>
            <tr>
              <th rowspan="2" class="align-middle">Particulars</th>
              <th rowspan="2" class="align-middle nowrap">Dep'n Rate %</th>
              <th colspan="4" class="text-center cost-band">Cost (fixed asset log)</th>
              <th colspan="4" class="text-center depn-band">Accumulated Depreciation (depn table)</th>
              <th rowspan="2" class="align-middle nowrap">Written Down Value</th>
            </tr>
            <tr>
              <th class="nowrap cost-band">Opening Balance</th>
              <th class="nowrap cost-band">Addition</th>
              <th class="nowrap cost-band">Disposal</th>
              <th class="nowrap cost-band">Closing Balance</th>
              <th class="nowrap depn-band">Opening Balance</th>
              <th class="nowrap depn-band">Addition</th>
              <th class="nowrap depn-band">Disposal</th>
              <th class="nowrap depn-band">Closing Balance</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(row, i) in rows" :key="i">
              <td>{{ particularLabel(row) }}</td>
              <td class="text-center">{{ row.depreciation_rate }}%</td>
              <td class="text-end">{{ commaFormat(row.cost_opening) }}</td>
              <td class="text-end">{{ commaFormat(row.cost_addition) }}</td>
              <td class="text-end">{{ commaFormat(row.cost_disposal) }}</td>
              <td class="text-end">{{ commaFormat(row.cost_closing) }}</td>
              <td class="text-end">{{ commaFormat(row.depreciation_opening) }}</td>
              <td class="text-end">{{ commaFormat(row.depreciation_addition) }}</td>
              <td class="text-end">{{ commaFormat(row.depreciation_disposal) }}</td>
              <td class="text-end">{{ commaFormat(row.depreciation_closing) }}</td>
              <td class="text-end">{{ commaFormat(row.written_down_value) }}</td>
            </tr>
            <tr v-if="hasRows" class="fw-bold total-row">
              <td>Total</td>
              <td></td>
              <td class="text-end">{{ commaFormat(total.cost_opening) }}</td>
              <td class="text-end">{{ commaFormat(total.cost_addition) }}</td>
              <td class="text-end">{{ commaFormat(total.cost_disposal) }}</td>
              <td class="text-end">{{ commaFormat(total.cost_closing) }}</td>
              <td class="text-end">{{ commaFormat(total.depreciation_opening) }}</td>
              <td class="text-end">{{ commaFormat(total.depreciation_addition) }}</td>
              <td class="text-end">{{ commaFormat(total.depreciation_disposal) }}</td>
              <td class="text-end">{{ commaFormat(total.depreciation_closing) }}</td>
              <td class="text-end">{{ commaFormat(total.written_down_value) }}</td>
            </tr>
            </tbody>
          </table>
          <p v-if="!hasRows" class="text-center mt-5">No data</p>
        </div>
      </div>
    </div>

    <ContactDropdownFilter
        v-if="store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        type="customer"
    />
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue'
import {useRoute} from 'vue-router'
import {useStore} from 'vuex'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import handleAssetRegister from '@/services/modules/assetRegister/assetRegister'
import handleChartAccount from '@/services/modules/chartAccount'
import handleCompany from '@/services/modules/company'
import handleContact from '@/services/modules/contact'
import handlePurchaseAndSales from '@/services/modules/purchase'
import handleBusinessesLocations from '@/services/modules/businessesLocations'
import handleDepartments from '@/services/modules/procurement/organization-settings/department'
import handleCostCentres from '@/services/modules/ngo/costCentre'
import handleProjects from '@/services/modules/procurement/project'
import figureFormatter from '@/services/utils/figureFormatter'

const $route = useRoute()
const store = useStore()
const showError = inject('showError')

const {fetchAssetScheduleReport} = handleAssetRegister()
const {fetchHome} = handleChartAccount()
const {getCompanyPreferences} = handleCompany()
const {fetchContactProfiles} = handleContact()
const {fetchProductList} = handlePurchaseAndSales()
const {fetchBusinessLocationsList} = handleBusinessesLocations()
const {fetchDepartmentList} = handleDepartments()
const {fetchCostCentreList} = handleCostCentres()
const {fetchProjects, fetchBusinesses} = handleProjects()
const {commaFormat} = figureFormatter()

const months = ref([])
const monthKeys = computed(() => months.value.map(month => month.value))
const monthLabels = computed(() => months.value.map(month => month.name.slice(0, 3)))

const loading = ref(false)
const rows = ref([])
const total = ref({})
const fyStartMonth = ref(1)
const interval = ref('')
const intervalValue = ref('')
const startDate = ref('')
const endDate = ref('')
const groupBy = ref('class')
const selectedProduct = ref(null)
const businessLocation = ref(null)
const department = ref(null)
const project = ref(null)
const costCentre = ref(null)
const business = ref(null)
const contactProfile = ref(null)

const groupByOptions = [
  {name: 'Asset Class Wise', value: 'class'},
  {name: 'Asset Register Wise', value: 'asset'}
]

const hasRows = computed(() => rows.value.length > 0)
const isAssetWise = computed(() => groupBy.value === 'asset')
const isYearly = computed(() => interval.value === 'yearly')
const isQuarterly = computed(() => interval.value === 'quarterly')
const isMonthly = computed(() => interval.value === 'monthly')
const isDaily = computed(() => interval.value === 'daily')

const yearOptions = computed(() => {
  const current = new Date().getFullYear()
  const list = []
  let year = current + 1
  while (year >= current - 8) {
    list.push({name: buildYearLabel(year), value: String(year)})
    year = year - 1
  }
  return list
})

const buildYearLabel = (year) => {
  const startIndex = fyStartMonth.value - 1
  if (startIndex === 0) return String(year)
  return `${monthLabels.value[startIndex]} ${year} - ${monthLabels.value[(startIndex + 11) % 12]} ${year + 1}`
}

const buildQuarterLabel = (startYear, quarter) => {
  const startIndex = (fyStartMonth.value - 1) + 3 * (quarter - 1)
  const endIndex = startIndex + 2
  const startLabel = `${monthLabels.value[startIndex % 12]} ${startYear + Math.floor(startIndex / 12)}`
  const endLabel = `${monthLabels.value[endIndex % 12]} ${startYear + Math.floor(endIndex / 12)}`
  return `Q${quarter} (${startLabel} - ${endLabel})`
}

const quarterOptions = computed(() => {
  const current = new Date().getFullYear()
  const list = []
  let year = current + 1
  while (year >= current - 8) {
    for (let quarter = 1; quarter <= 4; quarter++) {
      list.push({name: `${year} ${buildQuarterLabel(year, quarter)}`, value: `${year}-Q${quarter}`})
    }
    year = year - 1
  }
  return list
})

const particularLabel = (row) => {
  if (!isAssetWise.value) return classLabel(row)
  return `${row.asset_name} (${row.product_name})`
}

const classLabel = (row) => {
  if (!row.asset_class_name) return row.product_name
  return `${row.product_name} (${row.asset_class_name})`
}

const getQuery = () => {
  let query = `?company_id=${$route.params.companyId}`
  query += `&group_by=${groupBy.value}`
  if (intervalValue.value) query += `&interval_value=${intervalValue.value}`
  if (startDate.value) query += `&start_date=${startDate.value}`
  if (endDate.value) query += `&end_date=${endDate.value}`
  if (selectedProduct.value) query += `&product_id=${selectedProduct.value.id}`
  if (businessLocation.value) query += `&business_location_id=${businessLocation.value.id}`
  if (department.value) query += `&department_id=${department.value.id}`
  if (project.value) query += `&project_master_id=${project.value.id}`
  if (costCentre.value) query += `&cost_centre_id=${costCentre.value.id}`
  if (business.value) query += `&business_id=${business.value.id}`
  if (contactProfile.value) query += `&contact_profile_id=${contactProfile.value.id}`
  return query
}

const resetReport = () => {
  rows.value = []
  total.value = {}
}

const getReport = () => {
  if (isDaily.value && (!startDate.value || !endDate.value)) {
    showError('Please select start and end date')
    return
  }
  if (!isDaily.value && !intervalValue.value) {
    showError('Please select the report period')
    return
  }
  loading.value = true
  fetchAssetScheduleReport(getQuery())
      .then(res => {
        if (!res.status) {
          resetReport()
          showError(res.message)
          return
        }
        rows.value = res.data.rows
        total.value = res.data.total
      })
      .finally(() => loading.value = false)
}

const getPolicyPreferences = () => {
  const query = `?company_id=${$route.params.companyId}&fields=depreciation_interval,financial_year_start_month`
  return getCompanyPreferences(query).then(res => {
    if (!res.data) return
    setDepreciationInterval(res.data)
    setFyStartMonth(res.data)
  })
}

const setDepreciationInterval = (data) => {
  const item = data.find(item => item.field === 'depreciation_interval')
  if (!item) return
  interval.value = item.value
}

const setFyStartMonth = (data) => {
  const item = data.find(item => item.field === 'financial_year_start_month')
  if (!item) return
  const index = monthKeys.value.indexOf(String(item.value).toLowerCase())
  if (index < 0) return
  fyStartMonth.value = index + 1
}

const getMonths = () => {
  return fetchHome().then(res => {
    if (!res.data || !res.data.months) return
    months.value = res.data.months
  })
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

onMounted(() => {
  getMonths().then(() => getPolicyPreferences())
})
</script>

<style scoped>
.nowrap {
  white-space: nowrap;
}
.table-min-height {
  min-height: 320px;
}
.schedule-table th {
  vertical-align: middle;
}
.cost-band {
  background-color: #fdf6e3;
}
.depn-band {
  background-color: #eaf3e7;
}
.total-row td {
  background-color: #f2f2f2;
}
</style>
